import React, { useRef, useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Quill, getFormatsFromTools } from './Quill';
import { debounce } from '../../util/fnc/debounce';
import * as Ui from './styles.js';
import RichTextToolbar from './RichTextToolbar';
import { MAX_CHARACTERS } from '../Card/CardAnnotations/const';

const propTypes = {
  initialText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      ops: PropTypes.arrayOf(
        PropTypes.shape({
          insert: PropTypes.string.isRequired,
          attributes: PropTypes.object,
        }),
      ),
    }),
  ]),
  placeholder: PropTypes.string,
  tools: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
};

const defaultProps = {
  placeholder: 'Insira suas informações...',
  tools: [],
};

function RichTextBox(props) {
  const { onChange, initialText, placeholder, tools } = props;
  const DEBOUNCE_CALLBACK_TIME = 500;
  const debouncedOnChange = useRef();
  const quill = useRef();

  const editorRef = useRef();
  const toolbarRef = useRef();

  const [isOverLimit, setIsOverLimit] = useState(false);

  const buildDebouncedOnChange = useCallback(() => {
    let newOnchangeCallback = null;
    if (onChange) {
      newOnchangeCallback = debounce(onChange, DEBOUNCE_CALLBACK_TIME);
    }
    return newOnchangeCallback;
  }, [onChange]);

  useEffect(() => {
    debouncedOnChange.current = buildDebouncedOnChange();
  }, [onChange, buildDebouncedOnChange]);

  const initQuill = useCallback(() => {
    const options = {
      modules: {
        toolbar: {
          container: toolbarRef.current,
        },
      },
      formats: [],
      placeholder,
      theme: 'snow',
    };

    const newQuill = new Quill(editorRef.current, options);
    newQuill.clipboard.addMatcher('img', () => new Quill.imports.delta());
    newQuill.on('text-change', onTextChange);

    /**
     * Lida com diferentes formatos de `initialText`
     * OBS: Solução temporária para evitar quebra da funcionalidade,
     * componente será totalmente refatorado
     */
    if (typeof initialText === 'string') {
      newQuill.setContents({ ops: [{ insert: initialText }] });
    } else if (initialText?.ops) {
      newQuill.setContents(initialText);
    }

    quill.current = newQuill;
  }, [initialText, placeholder]);

  useEffect(() => {
    if (!editorRef.current || !toolbarRef.current) return;
    initQuill();
  }, [editorRef, toolbarRef, initQuill]);

  function onTextChange() {
    if (!quill.current) return;

    quill.current.off('text-change', onTextChange);

    try {
      const delta = quill.current.getContents();
      const filteredDelta = new Quill.imports.delta();
      let currentLength = 0;

      delta.ops.forEach(op => {
        if (typeof op.insert === 'string') {
          const remainingChars = MAX_CHARACTERS.OVER - currentLength;

          if (remainingChars > 0) {
            const sanitizedText = op.insert.replace(/['"]/g, '');

            const textToInsert = sanitizedText.slice(0, remainingChars);
            filteredDelta.insert(textToInsert);
            currentLength += textToInsert.length;
          }
        }
      });

      if (JSON.stringify(filteredDelta) !== JSON.stringify(delta)) {
        quill.current.setContents(filteredDelta);
      }
      setIsOverLimit(currentLength > MAX_CHARACTERS.LIMIT);
      quill.current.setSelection(currentLength);
      const plainText = quill.current.getText().trim();
      debouncedOnChange.current && debouncedOnChange.current(plainText);
    } catch (err) {
      console.error('Erro ao processar texto:', err);
    } finally {
      quill.current.on('text-change', onTextChange);
    }
  }

  return (
    <Ui.Container className="RichTextBox-container" hasError={isOverLimit}>
      <RichTextToolbar ref={toolbarRef} tools={tools} />
      <div ref={editorRef} />
    </Ui.Container>
  );
}

RichTextBox.propTypes = propTypes;
RichTextBox.defaultProps = defaultProps;
export default RichTextBox;